import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { UserPlus, Check, Clock, X, Ban } from 'lucide-react'
import { useFriendRequests } from '../hooks/useFriendRequests'

export default function GlobalSearch({ userId, onSelectResult, toast }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState({
    users: [],
    chats: [],
    spaces: [],
    channels: []
  })
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [friendshipStates, setFriendshipStates] = useState({}) // Track friendship status for each user
  const searchRef = useRef(null)

  // Use friend requests hook
  const friendRequests = useFriendRequests(userId, toast)

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults({ users: [], chats: [], spaces: [], channels: [] })
      return
    }

    const timeoutId = setTimeout(() => {
      performSearch()
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const performSearch = async () => {
    setLoading(true)
    try {
      // Search users/profiles (exclude self)
      const { data: users } = await supabase
        .from('profiles')
        .select('*')
        .ilike('username', `%${searchQuery}%`)
        .neq('id', userId)
        .limit(10)

      // Get friendship status for each user
      if (users && users.length > 0) {
        const userIds = users.map(u => u.id)
        const { data: friendships } = await supabase
          .from('friendships')
          .select('*')
          .or(`user_id.eq.${userId},friend_id.eq.${userId}`)
          .in('friend_id', userIds.concat([userId]))
          .in('user_id', userIds.concat([userId]))

        // Build friendship state map
        const stateMap = {}
        users.forEach(user => {
          const outgoing = friendships?.find(f => f.user_id === userId && f.friend_id === user.id)
          const incoming = friendships?.find(f => f.user_id === user.id && f.friend_id === userId)

          if (outgoing?.status === 'blocked' || incoming?.status === 'blocked') {
            stateMap[user.id] = { status: 'blocked', friendshipId: outgoing?.id || incoming?.id }
          } else if (outgoing?.status === 'pending') {
            stateMap[user.id] = { status: 'pending_sent', friendshipId: outgoing.id }
          } else if (incoming?.status === 'pending') {
            stateMap[user.id] = { status: 'pending_received', friendshipId: incoming.id }
          } else if (outgoing?.status === 'accepted' || incoming?.status === 'accepted') {
            stateMap[user.id] = { status: 'friends', friendshipId: outgoing?.id || incoming?.id }
          } else {
            stateMap[user.id] = { status: 'none' }
          }
        })
        setFriendshipStates(stateMap)
      }

      // Search spaces
      const { data: spaces } = await supabase
        .from('spaces')
        .select('*')
        .ilike('name', `%${searchQuery}%`)
        .limit(5)

      // Search channels
      const { data: channels } = await supabase
        .from('channels')
        .select('*, spaces(name)')
        .ilike('name', `%${searchQuery}%`)
        .limit(5)

      // Search DM channels (friend chats)
      const { data: dmChannels } = await supabase
        .from('dm_channels')
        .select('*, profiles!dm_channels_user1_id_fkey(username, avatar_url), profiles_user2:profiles!dm_channels_user2_id_fkey(username, avatar_url)')
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .limit(5)

      setResults({
        users: users || [],
        chats: dmChannels || [],
        spaces: spaces || [],
        channels: channels || []
      })
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleResultClick = (type, item) => {
    onSelectResult?.(type, item)
    setIsOpen(false)
    setSearchQuery('')
  }

  const getTotalResults = () => {
    return results.users.length + results.chats.length + results.spaces.length + results.channels.length
  }

  const filteredResults = () => {
    if (activeTab === 'all') return results
    return { ...results, [activeTab]: results[activeTab] }
  }

  return (
    <div ref={searchRef} className="relative">
      {/* Search Button/Input */}
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 w-full px-3 py-2 glass hover:glass-strong rounded-gjyl transition-all group"
        >
          <svg className="w-4 h-4 text-slate-500 group-hover:text-gjyl-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-slate-500 group-hover:text-white transition-colors text-xs">Find friends...</span>
        </button>
      ) : (
        <div className="p-2 glass rounded-gjyl">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Find friends..."
              className="w-full pl-8 pr-2 py-1.5 bg-gjyl-slate-800/50 text-white text-xs placeholder-slate-500 rounded border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* Search Dropdown */}
      {isOpen && searchQuery && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-gjyl-slate-900 border-2 border-gjyl-slate-700 rounded-gjyl shadow-gjyl-lift-strong z-50 overflow-hidden animate-scale-in max-h-80">
          {/* Results */}
          <div className="max-h-80 overflow-y-auto scrollbar-gjyl">
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gjyl-indigo-500"></div>
              </div>
            ) : getTotalResults() === 0 ? (
              <div className="p-4 text-center">
                <p className="text-xs text-slate-400">No users found</p>
              </div>
            ) : (
              <div className="py-1">
                {/* Users - Primary focus with friend request features */}
                {results.users.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase">
                      Users
                    </div>
                    {results.users.map((user) => {
                      const friendState = friendshipStates[user.id] || { status: 'none' }
                      return (
                        <div
                          key={user.id}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gjyl-slate-800 transition-all group"
                        >
                          <button
                            onClick={() => handleResultClick('user', user)}
                            className="flex items-center gap-2 flex-1 min-w-0"
                          >
                            <img
                              src={user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`}
                              alt={user.username}
                              className="w-8 h-8 rounded-gjyl ring-1 ring-gjyl-slate-700 group-hover:ring-gjyl-indigo-500 transition-all flex-shrink-0"
                            />
                            <div className="flex-1 text-left min-w-0">
                              <div className="text-sm font-medium text-white truncate">@{user.username}</div>
                              {user.custom_status && (
                                <div className="text-xs text-slate-500 truncate">{user.custom_status}</div>
                              )}
                            </div>
                          </button>

                          {/* Friend status badges/actions */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {friendState.status === 'friends' && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-400">
                                <Check className="w-3 h-3" />
                                <span>Friends</span>
                              </div>
                            )}
                            {friendState.status === 'pending_sent' && (
                              <>
                                <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                                  <Clock className="w-3 h-3" />
                                  <span>Pending</span>
                                </div>
                                <button
                                  onClick={() => friendRequests.cancelRequest(friendState.friendshipId).then(() => performSearch())}
                                  className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                  title="Cancel request"
                                >
                                  <X className="w-4 h-4 text-red-400" />
                                </button>
                              </>
                            )}
                            {friendState.status === 'pending_received' && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-400">
                                <Clock className="w-3 h-3" />
                                <span>Incoming</span>
                              </div>
                            )}
                            {friendState.status === 'blocked' && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                                <Ban className="w-3 h-3" />
                                <span>Blocked</span>
                              </div>
                            )}
                            {friendState.status === 'none' && (
                              <button
                                onClick={() => friendRequests.sendRequest(user.id, user.username).then(() => performSearch())}
                                disabled={friendRequests.loading}
                                className="flex items-center gap-1 px-2 py-1 bg-gjyl-indigo-500/20 hover:bg-gjyl-indigo-500/30 border border-gjyl-indigo-500/30 hover:border-gjyl-indigo-500/50 rounded text-xs text-gjyl-indigo-300 hover:text-gjyl-indigo-200 transition-all disabled:opacity-50"
                                title="Send friend request"
                              >
                                <UserPlus className="w-3 h-3" />
                                <span>Add Friend</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
